/**
 * Phase 286 D-REPORTING-01 — React Query hooks for the reporting surface.
 *
 * Mirrors the fetch pattern in queries.ts (fetchWithAuth<T>() + useQuery) but NOT its
 * cadence: these are report pages, not live-ops screens — see REPORTING_POLL_OPTIONS.
 *
 * Endpoint: GET /internal/v0/reporting/weekly-activity?tenant=<Guid>[&metric_family=<str>]
 * Response: WeeklyActivityGridResponse (cells + as_of + consultants).
 *
 * Auth: OperatorBearerFilter (JWT; inherited from the MapInternalApiOperator group).
 * Tenant guard: caller MUST supply a tenantId the operator is allowed to read.
 *   enabled: !!filter.tenantId guards against firing before /me resolves.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetcher.js";
import type { WeeklyActivityGridResponse, ForecastingRollupResponse } from "./types.js";

/**
 * Reporting refresh cadence — 60s, deliberately NOT the 4s D-FETCH-01 cadence the
 * live-ops screens (jobs, failures, drafts) use via POLL_OPTIONS in queries.ts.
 *
 * Two reasons the live-ops cadence is wrong here:
 *  1. Reporting facts change WEEKLY (the fact store is written by the weekly report
 *     run), so a 4s poll re-renders identical bytes ~900 times an hour. The 4s window
 *     (CON-07) exists for job/queue state an operator is watching move — not for a
 *     report page.
 *  2. Each weekly-activity read now resolves the consultant roster through the Loxo
 *     connector server-side (phase 286 wave 7), so the poll is no longer a pure
 *     fact-store read — a 4s cadence would hammer a third-party integration.
 *
 * Same call phase 336 made for the client reporting portal. staleTime matches the
 * interval so a remount inside the window serves cache instead of refetching.
 * Still paused when the tab is hidden (operators routinely have several tabs open).
 */
const REPORTING_POLL_OPTIONS = {
  refetchInterval: 60_000,
  refetchIntervalInBackground: false,
  staleTime: 60_000,
} as const;

/** Filter shape for GET /internal/v0/reporting/weekly-activity. */
export interface WeeklyActivityFilter {
  /** Tenant UUID string. Required. Hook is disabled until non-empty. */
  tenantId: string;
  /** Metric family discriminator. Defaults to "weekly-sales-activity" (engine default). */
  metricFamily?: string;
}

/**
 * Polling hook for GET /internal/v0/reporting/weekly-activity, on the 60s reporting
 * cadence (REPORTING_POLL_OPTIONS). Disabled until tenantId is non-empty (operator /me
 * hasn't resolved yet).
 *
 * Freshness disclosure comes from the WIRE, not from the query client: the screen renders
 * <LastUpdated timestamp={new Date(data.as_of)} /> — `data.as_of` is the snake_case
 * fact-store MAX(as_of) (ADR-068 D3 / ADR-074 D4), i.e. when the facts were computed,
 * which is what an operator needs to know. The client-side fetch timestamp is deliberately
 * NOT surfaced; it would report the poll, not the data.
 */
export function useWeeklyActivity(filter: WeeklyActivityFilter) {
  const qs = new URLSearchParams({ tenant: filter.tenantId });
  if (filter.metricFamily) qs.set("metric_family", filter.metricFamily);

  return useQuery({
    queryKey: ["weekly-activity", filter] as const,
    queryFn: () =>
      fetchWithAuth<WeeklyActivityGridResponse>(
        `/internal/v0/reporting/weekly-activity?${qs.toString()}`,
      ),
    ...REPORTING_POLL_OPTIONS,
    // Disable until tenantId is resolved (e.g., before /me returns).
    enabled: !!filter.tenantId,
  });
}

/** Filter shape for GET /internal/v0/reporting/forecasting. */
export interface ForecastingFilter {
  /** Tenant UUID string. Required. Hook is disabled until non-empty. */
  tenantId: string;
  /** Metric family discriminator. Defaults to "sales-forecast" (engine default). */
  metricFamily?: string;
}

/**
 * Polling hook for GET /internal/v0/reporting/forecasting, on the 60s reporting cadence
 * (REPORTING_POLL_OPTIONS). Disabled until tenantId is non-empty (operator /me hasn't
 * resolved yet).
 *
 * Renders empty-state until Phase 292 (#955) writes forecasting facts. When `data.as_of`
 * (the snake_case wire field) is null — no forecasting facts yet — the screen renders
 * "no forecasting data yet" rather than a blank LastUpdated.
 */
export function useForecastingRollup(filter: ForecastingFilter) {
  const qs = new URLSearchParams({ tenant: filter.tenantId });
  if (filter.metricFamily) qs.set("metric_family", filter.metricFamily);

  return useQuery({
    queryKey: ["forecasting-rollup", filter] as const,
    queryFn: () =>
      fetchWithAuth<ForecastingRollupResponse>(
        `/internal/v0/reporting/forecasting?${qs.toString()}`,
      ),
    ...REPORTING_POLL_OPTIONS,
    // Disable until tenantId is resolved (e.g., before /me returns).
    enabled: !!filter.tenantId,
  });
}
